// session/session.jsx — 세션과 **사람 Node 해소**를 한 자리에서 판별 가능한 상태로 만든다.
//
// ux §3.1 이 요구하는 세 갈림길(`human_node_absent` · `human_node_selection_required` ·
// `space_not_projected`)이 화면 코드에서 if 문으로 흩어지지 않게 하는 것이 목적이다.
// 갈림길이 흩어지면 화면마다 다르게 갈리고, 그게 QA v2 의 D-3(Space 0개인 사람에게
// 진입로가 없다)이 난 방식이다.
//
// 여기서 절대 하지 않는 것:
//   - nodeId 를 화면에 보이거나 localStorage 에 저장하는 것. nodeId 는 "지금 어느 Node 로
//     말하는가"를 매 요청 지정하는 **배선 값**이지 사람이 읽을 정보가 아니다(ux §5 규칙 6).
//   - 후보가 여럿일 때 하나를 골라 주는 것. 서버도 그렇게 하지 않는다 — 애매할 때 골라 주면
//     그 선택은 사람이 한 적 없는 선택이고, 그 뒤의 모든 판단이 그 위에 쌓인다.

import { createContext } from 'preact';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import {
  login,
  logout as apiLogout,
  read,
  startSessionKeepalive,
  stopSessionKeepalive,
  whoami,
} from '../api/client.js';
import { describeError, SCOPE } from '../api/errors.js';
import { invalidateAll } from '../api/resource.js';
import { clearAllDrafts } from '../routes/task-drafts.js';

/** 세션 상태. 이 여섯 말고는 없다. */
export const SESSION = Object.freeze({
  LOADING: 'loading',
  ANONYMOUS: 'anonymous',
  NEEDS_ONBOARDING: 'needs_onboarding',
  NEEDS_NODE_SELECTION: 'needs_node_selection',
  READY: 'ready',
  ERROR: 'error',
});

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [state, setState] = useState({ status: SESSION.LOADING });

  const bootstrap = useCallback(async (preferredNodeId) => {
    setState({ status: SESSION.LOADING });

    // 1) 세션이 있나. whoami 는 csrfToken 과 만료 시각만 준다 — 신원은 나오지 않는다.
    try {
      const who = await whoami();
      if (who?.authenticated !== true) {
        setState({ status: SESSION.ANONYMOUS });
        return;
      }
    } catch (error) {
      const d = describeError(error);
      if (d.scope === SCOPE.SESSION) {
        setState({ status: SESSION.ANONYMOUS });
      } else {
        setState({ status: SESSION.ERROR, error });
      }
      return;
    }

    // 2) 이 anchor 의 사람 Node 를 해소한다. 이 호출만 nodeId 헤더 없이 나간다.
    try {
      const entry = await read('entry', {
        query: preferredNodeId ? { nodeId: preferredNodeId } : undefined,
      });
      setState({
        status: SESSION.READY,
        node: entry.node,
        // 서버 응답의 상위 키는 배포에 따라 memberships 또는 spaces 다. 둘 다 받는다.
        spaces: entry.memberships ?? entry.spaces ?? [],
        channel: entry.channel ?? null,
      });
    } catch (error) {
      const d = describeError(error);
      if (error?.code === 'human_node_absent') {
        setState({ status: SESSION.NEEDS_ONBOARDING });
        return;
      }
      if (error?.code === 'human_node_selection_required' || error?.code === 'human_node_not_selectable') {
        setState({
          status: SESSION.NEEDS_NODE_SELECTION,
          candidates: d.candidates ?? [],
          error: error?.code === 'human_node_not_selectable' ? error : null,
        });
        return;
      }
      if (d.scope === SCOPE.SESSION) {
        setState({ status: SESSION.ANONYMOUS });
        return;
      }
      setState({ status: SESSION.ERROR, error });
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // 세션 유지. idle 만료(30분) 한참 전에 회전시킨다.
  // absolute TTL(12시간)·max auth age(1시간)는 이걸로 넘지 못한다 — 그때는 재로그인만이 답이다.
  useEffect(() => {
    if (state.status !== SESSION.READY) return undefined;
    startSessionKeepalive(() => {
      invalidateAll();
      // 세션이 끝났다. 아직 안 보낸 글도 함께 버린다 — 다음 사람의 화면에 남을 자리가 없다.
      clearAllDrafts();
      setState({ status: SESSION.ANONYMOUS });
    });
    return stopSessionKeepalive;
  }, [state.status]);

  const selectNode = useCallback((nodeId) => bootstrap(nodeId), [bootstrap]);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      invalidateAll();
      clearAllDrafts();
      setState({ status: SESSION.ANONYMOUS });
    }
  }, []);

  const value = {
    ...state,
    /** 모든 읽기·쓰기에 실어 보내는 배선 값. 화면에 그리지 않는다. */
    nodeId: state.status === SESSION.READY ? state.node.nodeId : null,
    reload: () => bootstrap(),
    selectNode,
    logout,
    login,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/**
 * 읽기 훅에 쓸 nodeId. 세션이 준비되기 전에는 `null` 이고,
 * `useResource` 의 key 가 `null` 이면 읽지 않는다 — 그래서 "nodeId 없이 나가는 요청"이
 * 구조적으로 생기지 않는다.
 */
export function useNodeId() {
  return useSession().nodeId;
}
